import { toPlainText } from './richText';
/**
 * Returns every problem found, rather than throwing on the first.
 *
 * A recovery screen needs to tell the user what is wrong with their file; stopping at the first
 * issue turns a repairable document into a series of one-at-a-time failures.
 */
export function checkIntegrity(doc, options) {
    const issues = [];
    const nodeIds = Object.keys(doc.nodes);
    const root = doc.nodes[doc.map.rootNodeId];
    if (root === undefined) {
        issues.push({
            code: 'ROOT_MISSING',
            id: doc.map.rootNodeId,
            detail: 'map.rootNodeId does not resolve to a node',
        });
        // Without a root there is no tree to check the rest against.
        return issues;
    }
    if (root.parentId !== null) {
        issues.push({
            code: 'ROOT_HAS_PARENT',
            id: root.id,
            detail: `root must have parentId null, found ${root.parentId}`,
        });
    }
    for (const id of nodeIds) {
        const node = doc.nodes[id];
        if (node === undefined)
            continue;
        if (node.mapId !== doc.map.id) {
            issues.push({
                code: 'NODE_MAP_MISMATCH',
                id,
                detail: `node belongs to map ${node.mapId}, document is ${doc.map.id}`,
            });
        }
        if (node.parentId === null) {
            if (id !== root.id) {
                issues.push({ code: 'MULTIPLE_ROOTS', id, detail: 'a second node has no parent' });
            }
            continue;
        }
        if (doc.nodes[node.parentId] === undefined) {
            issues.push({
                code: 'PARENT_MISSING',
                id,
                detail: `parent ${node.parentId} does not exist`,
            });
        }
    }
    // Reachability and cycles in one walk from the root.
    const childrenByParent = new Map();
    for (const id of nodeIds) {
        const parentId = doc.nodes[id]?.parentId;
        if (parentId == null)
            continue;
        const bucket = childrenByParent.get(parentId);
        if (bucket === undefined)
            childrenByParent.set(parentId, [id]);
        else
            bucket.push(id);
    }
    const reachable = new Set();
    const stack = [root.id];
    while (stack.length > 0) {
        const current = stack.pop();
        if (current === undefined || reachable.has(current))
            continue;
        reachable.add(current);
        for (const child of childrenByParent.get(current) ?? [])
            stack.push(child);
    }
    for (const id of nodeIds) {
        if (reachable.has(id))
            continue;
        const node = doc.nodes[id];
        const parentId = node?.parentId ?? null;
        // An unreachable node whose parent exists is in a cycle; one whose parent is missing is an
        // orphan. Reporting the same symptom under one name would not help anyone repair it.
        const parentExists = parentId !== null && doc.nodes[parentId] !== undefined;
        issues.push({
            code: parentExists ? 'CYCLE' : 'ORPHAN',
            id,
            detail: parentExists
                ? 'not reachable from the root although its parent chain exists, so it forms a cycle'
                : 'not reachable from the root',
        });
    }
    // Two siblings sharing an order key have no defined relative position, so the outline and the
    // canvas would be free to disagree about which comes first.
    const seenOrders = new Map();
    for (const id of nodeIds) {
        const node = doc.nodes[id];
        if (node?.parentId == null)
            continue;
        const key = `${node.parentId} ${node.order}`;
        const existing = seenOrders.get(key);
        if (existing !== undefined) {
            issues.push({
                code: 'DUPLICATE_ORDER',
                id,
                detail: `shares order "${node.order}" with ${existing} under the same parent`,
            });
        }
        seenOrders.set(key, id);
    }
    for (const [edgeId, edge] of Object.entries(doc.edges)) {
        if (edge.sourceNodeId === edge.targetNodeId) {
            issues.push({ code: 'EDGE_SELF_LINK', id: edgeId, detail: 'edge connects a node to itself' });
        }
        if (doc.nodes[edge.sourceNodeId] === undefined) {
            issues.push({
                code: 'EDGE_ENDPOINT_MISSING',
                id: edgeId,
                detail: `source ${edge.sourceNodeId} does not exist`,
            });
        }
        if (doc.nodes[edge.targetNodeId] === undefined) {
            issues.push({
                code: 'EDGE_ENDPOINT_MISSING',
                id: edgeId,
                detail: `target ${edge.targetNodeId} does not exist`,
            });
        }
    }
    // On a client, the server cannot have acknowledged edits this device never made.
    if (doc.map.serverRevision > doc.map.localRevision) {
        issues.push({
            code: 'REVISION_INVERTED',
            id: doc.map.id,
            detail: `serverRevision ${String(doc.map.serverRevision)} exceeds localRevision ${String(doc.map.localRevision)}`,
        });
    }
    if (options?.checkPlainText === true) {
        issues.push(...checkPlainTextConsistency(doc));
    }
    return issues;
}
/**
 * Verifies the denormalised `plainText` against its source.
 *
 * Search, embeddings, accessibility labels and text export all read `plainText`. If it drifts
 * from `richText`, all four go wrong at once and none of them look like the cause.
 */
export function checkPlainTextConsistency(doc) {
    const issues = [];
    for (const [id, node] of Object.entries(doc.nodes)) {
        const expected = toPlainText(node.richText);
        if (node.plainText !== expected) {
            issues.push({
                code: 'PLAIN_TEXT_DRIFT',
                id,
                detail: `plainText does not match richText (${String(node.plainText.length)} chars stored, ${String(expected.length)} derived)`,
            });
        }
    }
    return issues;
}
export function isIntact(doc, options) {
    return checkIntegrity(doc, options).length === 0;
}
//# sourceMappingURL=invariants.js.map